/**
 * Location health pieces (core locationhealth.ts; docs/LOCATION_HEALTH.md, STANDARDS S15): the glance line for Today,
 * Insights and the home's Rooms page, one item row, and the last health inspection. Every status is a color and a word,
 * never color alone; every row has one action that opens the screen that already does it.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { INSPECTION_RESULTS, showTemps, type HealthItem, type InspectionResult, type LocationHealth } from "@logwave/core";
import { useTempUnit } from "../lib/units.ts";
import { Icon } from "./Icons.tsx";
import { Button, Chips, Field, InfoLine, LinkButton, Pill } from "./ui.tsx";
import { STATUS_ICON, VECTOR_ICON, healthActionPath } from "../lib/health.ts";
import { localDate } from "../lib/time.ts";
import { useLoad } from "../lib/useLoad.ts";
import { useApi, useSession } from "../session.tsx";

/** "Health: 2 things need you: Walk-in over 41°F · … ›". Quiet when it can't load (the page it sits on never breaks for it). */
export function HealthLine({ version = 0, home = false }: { version?: number; home?: boolean }) {
  const api = useApi();
  const s = useSession();
  const h = useLoad(() => (home ? api.homeHealth(s.org.id) : api.locationHealth(s.org.id, s.location.id)), [api, s.org.id, home ? "" : s.location?.id, version, home]);
  // Limits in the viewer's unit ("Walk-in over 5°C"; core temperature.ts T5).
  const [unit] = useTempUnit();
  if (!h.data) return null;
  const d = { ...h.data, line: showTemps(h.data.line, unit) };
  const title = home ? "Home health" : "Health";
  return (
    <Link to="/health" className={`card rb-line${d.tone === "stop" ? " edge edge-stop" : ""}`} aria-label={`${title}: ${d.word}. ${d.line}`}>
      <Icon name={STATUS_ICON[d.status]} size={24} />
      <span className="rb-line-text"><span className="kicker">{title}</span><strong>{d.line}</strong></span>
      <Pill tone={d.tone} icon={STATUS_ICON[d.status]}>{d.word}</Pill>
      <Icon name="chevron" size={20} />
    </Link>
  );
}

/** One item: what, its status word, its line (last checked, who), why it's owed, and one action. */
export function HealthItemRow({ item: raw, primary }: { item: HealthItem; primary?: boolean }) {
  const [unit] = useTempUnit();
  const item = { ...raw, line: showTemps(raw.line, unit), basisLine: showTemps(raw.basisLine, unit) };
  const tone = item.status === "failed" ? "stop" : item.status === "late" ? "warn" : item.status === "ok" ? "go" : "info";
  return (
    <li className="rb-step" data-status={item.status} data-id={item.id} data-gravity={item.grave ? "grave" : undefined}>
      <span className={`rb-icon${item.status === "failed" ? " rb-icon-grave" : item.status === "late" ? " rb-icon-required" : ""}`} aria-hidden="true"><Icon name={VECTOR_ICON[item.vector]} size={26} /></span>
      <div className="rb-body">
        <p className="rb-what">{item.name}</p>
        <p className="row"><Pill tone={tone} icon={STATUS_ICON[item.status]}>{item.word}</Pill></p>
        <p className="caption">{item.line}</p>
        <InfoLine label={item.basis === "sense" ? "Suggested" : "Why"} value={item.basisLine} icon={item.basis === "sense" ? "info" : "prove"} />
      </div>
      <div className="rb-action">
        <LinkButton to={healthActionPath(item.action)} variant={primary ? "primary" : "secondary"} icon="chevron" iconEnd>{item.action.label}</LinkButton>
      </div>
    </li>
  );
}

/** The last health department inspection: the line, and (manager+) enter or change it. */
export function InspectionCard({ view, onSaved }: { view: LocationHealth; onSaved: (v: LocationHealth) => void }) {
  const api = useApi();
  const s = useSession();
  const [open, setOpen] = useState(false);
  const [on, setOn] = useState(view.inspection?.on ?? "");
  const [result, setResult] = useState<InspectionResult | null>(view.inspection?.result ?? null);
  const [note, setNote] = useState(view.inspection?.note ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  if (view.scope === "home") return null;
  const today = localDate(new Date(), s.location.timezone);
  async function save() {
    if (!on || !result) { setError("Pick the date and how it went."); return; }
    setBusy(true); setError(undefined);
    try {
      const next = await api.putInspection(s.org.id, { locationId: s.location.id, on, result, ...(note.trim() ? { note: note.trim() } : {}) });
      onSaved(next);
      setOpen(false);
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); } finally { setBusy(false); }
  }
  return (
    <div className="stack-sm" aria-label="Last health inspection">
      <p className="row"><Icon name="prove" size={18} /><span>{view.inspectionLine}</span></p>
      {view.inspection?.note && <p className="caption">{view.inspection.note}</p>}
      {view.canEditInspection && !open && <Button small variant="secondary" icon={view.inspection ? "write" : "plus"} onClick={() => setOpen(true)}>{view.inspection ? "Change it" : "Add the last inspection"}</Button>}
      {open && (
        <div className="card card-flat stack-sm">
          <Field label="Date of the inspection">{(id) => <input id={id} type="date" max={today} value={on} onChange={(e) => setOn(e.target.value)} />}</Field>
          <Chips label="How did it go?" options={INSPECTION_RESULTS.map((r) => ({ value: r.key, label: r.label }))} value={result} onChange={setResult} />
          <Field label="A note (optional)">{(id) => <input id={id} type="text" maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} />}</Field>
          {error && <p className="error" role="alert">{error}</p>}
          <span className="row"><Button busy={busy} icon="check" onClick={save}>Save the inspection</Button><Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button></span>
        </div>
      )}
    </div>
  );
}
